import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import Buttons from 'mutation/components/cancel-civil-union/buttons';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import {
  checkPersonCancelCivilUnion,
  transformerCancelCivilUnionData,
} from 'mutation/utils/transformer-cancel-civil-union';
import ExitModal from 'mutation/components/shared/exit-modal';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformerRequestCancelCivilUnionData } from 'mutation/utils/transformer-request-cancel-civil-union';
import { toast } from 'react-toastify';
import { validationSchema } from 'mutation/constants/cancel-civil-union-validation';
import FormCancelCivilUnion from 'mutation/components/cancel-civil-union/form';
import { CancelCivilUnionFormVM } from 'mutation/view-models/cancel-civil-union';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CancelCivilUnionCrumbs } from 'mutation/constants/registration-cancel-civil-union-crumbs';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import ErrorModal from 'mutation/components/shared/error-modal';
import PmiTitle from 'shell/components/shared/title';

const CancelCivilUnionPage = (): React.ReactElement => {
  //Modal state
  const [open, setOpen] = useState<boolean>(false);

  const [queryParams] = useSearchParams();

  // query params
  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, CancelCivilUnionFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: transformerCancelCivilUnionData,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCivilUnion, cancelUnionCivil] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/scioglimento-unione-civile/registrazione`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedAnprData = responseCivilUnion.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const navigate = useNavigate();

  const handleSubmit = useEventCallback((values: CancelCivilUnionFormVM) => {
    const data = transformerRequestCancelCivilUnionData(values);

    (
      cancelUnionCivil({ data }) as ResponsePromise<
        HttpResponse<ResponseAnprSaveData>,
        SaveDataAnpr
      >
    ).then((response) => {
      const castedResponse = response as SaveDataAnpr;

      setVisibility({ error: undefined, warning: undefined });

      if (castedResponse?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }

      toast.success('Operazione eseguita con successo');
      navigate('/mutazioni/cerca-persona/unione-civile');
    });
  });

  const handleExit = useCallback(() => {
    navigate('/mutazioni/cerca-persona/unione-civile');
  }, [navigate]);

  const handleOpenModal = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const [openError, setOpenError] = useState<boolean>(false);

  useEffect(() => {
    const data = anpr.data as CancelCivilUnionFormVM;

    const canBeMutated = checkPersonCancelCivilUnion(data?.civilStatus?.oldStatus);

    if (!canBeMutated) {
      // persona già coniugata
      setOpenError(true);
    }
  }, [anpr.data]);

  const onCloseErrorModal = useEventCallback((value: boolean) => {
    setOpenError(value);
    navigate('/mutazioni/cerca-persona/unione-civile');
  });

  const isLoading = !anpr.data || anpr.isLoading;

  return !isLoading ? (
    <Fragment>
      <Breadcrumbs items={CancelCivilUnionCrumbs} />
      <PmiTitle title="Annullamento Unione Civile" />
      <ErrorModal
        open={openError}
        onClose={onCloseErrorModal}
        message="Stato civile del soggetto incongruente con l'annullamento dell'unione civile"
      />
      <Formik
        validationSchema={validationSchema}
        initialValues={anpr.data as CancelCivilUnionFormVM}
        onSubmit={handleSubmit}
      >
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <Buttons handleOpenModal={handleOpenModal} />
            </Grid>
            <Grid item xs={12} md={10}>
              <SearchMessages
                type={MessageType.Error}
                title={errorMessage.title || ''}
                messages={errorMessage.messages || []}
                visibility={errorMessage.visibility || false}
                onChangeVisibility={onVisibility}
              />
              <SearchMessages
                type={MessageType.Warning}
                title={
                  castedAnprData?.warnings?.length === 1
                    ? `C'è 1 warning`
                    : `Ci sono ${castedAnprData?.warnings?.length} warning`
                }
                messages={castedAnprData?.warnings || []}
                visibility={
                  visibility.warning !== undefined
                    ? visibility.warning
                    : (castedAnprData?.warnings || [])?.length > 0
                }
                onChangeVisibility={onVisibility}
              />
              <FormCancelCivilUnion />
            </Grid>
          </Grid>
          <ExitModal open={open} onClose={() => setOpen(false)} onConfirm={handleExit} />
        </Form>
      </Formik>
    </Fragment>
  ) : (
    <></>
  );
};

CancelCivilUnionPage.displayName = 'Cancel Civil union page component';

export default CancelCivilUnionPage;
